import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Shield,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Download,
  Eye,
  User,
  Building,
  FileText,
  Calendar,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminKYCReviewPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [pendingDocs, setPendingDocs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState({ open: false, doc: null });
  const [reviewAction, setReviewAction] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch pending submissions
  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/kyc/pending`, { headers });
      setPendingDocs(response.data);
    } catch (error) {
      console.error('Failed to fetch pending KYC:', error);
    }
  };

  // Fetch all users summary
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/kyc/users`, { headers });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch KYC users:', error);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchPending(), fetchUsers()]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocTypeName = (docType) => {
    const names = {
      id_document: 'ID / Passport',
      proof_of_address: 'Proof of Address',
      source_of_funds: 'Source of Funds',
      tax_clearance: 'Tax Clearance',
      bank_confirmation: 'Bank Confirmation',
      registration_documents: 'Registration Documents',
      resolution: 'Resolution',
      beneficial_ownership: 'Beneficial Ownership'
    };
    return names[docType] || docType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getFicaStatusConfig = (status) => {
    switch (status) {
      case 'compliant':
        return { label: 'Compliant', color: 'bg-green-100 text-green-700 border-green-200', icon: ShieldCheck };
      case 'pending':
        return { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      case 'expired':
        return { label: 'Expired', color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert };
      default:
        return { label: 'Unknown', color: 'bg-gray-100 text-gray-700', icon: Shield };
    }
  };

  const handleReview = async () => {
    if (!reviewDialog.doc || !reviewAction) return;

    if (reviewAction === 'reject' && !reviewComments.trim()) {
      toast.error('Please provide a rejection reason.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/kyc/${reviewDialog.doc.id}/review`, {
        action: reviewAction,
        rejection_reason: reviewAction === 'reject' ? (reviewComments || null) : null,
        expiry_date: expiryDate || null
      }, { headers });

      toast.success(`Document ${reviewAction === 'approve' ? 'approved' : 'rejected'} successfully!`);
      setReviewDialog({ open: false, doc: null });
      setReviewAction('');
      setReviewComments('');
      setExpiryDate('');
      fetchAll();
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/kyc/documents/${docId}/download`, {
        headers,
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'kyc_document';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document.');
    }
  };

  const handleViewDocument = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/kyc/documents/${docId}/download`, {
        headers,
        responseType: 'blob'
      });
      
      // Open in new tab for viewing
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to view document.');
    }
  };

  const filteredUsers = allUsers.filter(user => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      user.user?.email?.toLowerCase().includes(search) ||
      user.user?.first_name?.toLowerCase().includes(search) ||
      user.user?.last_name?.toLowerCase().includes(search) ||
      user.profile?.entity_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6" data-testid="admin-kyc-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">KYC Review Queue</h1>
          <p className="text-[#6C757D] mt-1">Review and approve investor KYC document submissions</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          className="border-gray-200"
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]" data-testid="pending-count">
                  {pendingDocs.length}
                </p>
                <p className="text-xs text-[#6C757D]">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {allUsers.filter(u => u.fica_status === 'compliant').length}
                </p>
                <p className="text-xs text-[#6C757D]">Compliant Investors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {allUsers.filter(u => u.expired > 0).length}
                </p>
                <p className="text-xs text-[#6C757D]">Expired Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#212529]">
                  {allUsers.length}
                </p>
                <p className="text-xs text-[#6C757D]">Total Investors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white" data-testid="tab-pending">
            Pending Review ({pendingDocs.length})
          </TabsTrigger>
          <TabsTrigger value="investors" className="data-[state=active]:bg-white" data-testid="tab-investors">
            All Investors
          </TabsTrigger>
        </TabsList>

        {/* Pending Review Tab */}
        <TabsContent value="pending">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : pendingDocs.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <p className="text-[#212529] font-medium">All caught up!</p>
                  <p className="text-[#6C757D] text-sm">No pending KYC documents to review.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Investor</TableHead>
                      <TableHead>Document Type</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingDocs.map((doc) => (
                      <TableRow key={doc.id} data-testid={`pending-row-${doc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#1D2D2A]/10 flex items-center justify-center">
                              {doc.profile?.entity_type === 'individual' ? (
                                <User className="h-4 w-4 text-[#1D2D2A]" />
                              ) : (
                                <Building className="h-4 w-4 text-[#1D2D2A]" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-[#212529]">
                                {doc.user?.first_name} {doc.user?.last_name}
                              </p>
                              <p className="text-xs text-[#6C757D]">{doc.user?.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{getDocTypeName(doc.document_type)}</p>
                            <p className="text-xs text-[#6C757D]">v{doc.version}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[#6C757D]" />
                            <div>
                              <p className="text-sm truncate max-w-[150px]">{doc.file_name}</p>
                              <p className="text-xs text-[#6C757D]">{formatFileSize(doc.file_size)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#6C757D]">
                          {formatDate(doc.uploaded_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDocument(doc.id)}
                              className="border-gray-200"
                              title="View Document"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(doc.id)}
                              className="border-gray-200"
                              title="Download Document"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                              onClick={() => {
                                setReviewDialog({ open: true, doc });
                                setReviewAction('');
                                setReviewComments('');
                                setExpiryDate('');
                              }}
                              data-testid={`review-btn-${doc.id}`}
                            >
                              Review
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Investors Tab */}
        <TabsContent value="investors">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Investor KYC Status</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#ADB5BD]" />
                  <Input
                    placeholder="Search investors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-gray-200"
                    data-testid="search-investors"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Investor</TableHead>
                      <TableHead>Entity Type</TableHead>
                      <TableHead>FICA Status</TableHead>
                      <TableHead className="text-center">Documents</TableHead>
                      <TableHead className="text-center">Pending</TableHead>
                      <TableHead className="text-center">Expired</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((item) => {
                      const statusConfig = getFicaStatusConfig(item.fica_status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={item.user?.id} data-testid={`investor-row-${item.user?.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#1D2D2A]/10 flex items-center justify-center">
                                {item.entity_type === 'individual' ? (
                                  <User className="h-4 w-4 text-[#1D2D2A]" />
                                ) : (
                                  <Building className="h-4 w-4 text-[#1D2D2A]" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-[#212529]">
                                  {item.user?.first_name} {item.user?.last_name}
                                </p>
                                <p className="text-xs text-[#6C757D]">{item.user?.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{item.entity_type}</TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig.color} border`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-medium text-green-600">{item.approved}</span>
                            <span className="text-[#6C757D]">/{item.required_documents}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {item.pending > 0 ? (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 border">
                                {item.pending}
                              </Badge>
                            ) : (
                              <span className="text-[#6C757D]">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.expired > 0 ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200 border">
                                {item.expired}
                              </Badge>
                            ) : (
                              <span className="text-[#6C757D]">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => {
        if (!open) {
          setReviewDialog({ open: false, doc: null });
          setReviewAction('');
          setReviewComments('');
          setExpiryDate('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Review KYC Document</DialogTitle>
            <DialogDescription>
              Review and approve or reject this submission
            </DialogDescription>
          </DialogHeader>
          
          {reviewDialog.doc && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-8 w-8 text-[#1D2D2A]" />
                  <div>
                    <p className="font-medium">{reviewDialog.doc.file_name}</p>
                    <p className="text-sm text-[#6C757D]">
                      {getDocTypeName(reviewDialog.doc.document_type)} • {formatFileSize(reviewDialog.doc.file_size)}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#6C757D]">Investor: </span>
                    <span className="font-medium">{reviewDialog.doc.user?.first_name} {reviewDialog.doc.user?.last_name}</span>
                  </div>
                  <div>
                    <span className="text-[#6C757D]">Uploaded: </span>
                    <span className="font-medium">{formatDate(reviewDialog.doc.uploaded_at)}</span>
                  </div>
                </div>
              </div>

              {/* Action Selection */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-[#212529]">Select Action</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      reviewAction === 'approve'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setReviewAction('approve')}
                    data-testid="action-approve"
                  >
                    <CheckCircle2 className={`h-6 w-6 mx-auto mb-2 ${
                      reviewAction === 'approve' ? 'text-green-600' : 'text-[#6C757D]'
                    }`} />
                    <p className={`font-medium ${
                      reviewAction === 'approve' ? 'text-green-700' : 'text-[#212529]'
                    }`}>Approve</p>
                  </button>
                  <button
                    type="button"
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      reviewAction === 'reject'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setReviewAction('reject')}
                    data-testid="action-reject"
                  >
                    <XCircle className={`h-6 w-6 mx-auto mb-2 ${
                      reviewAction === 'reject' ? 'text-red-600' : 'text-[#6C757D]'
                    }`} />
                    <p className={`font-medium ${
                      reviewAction === 'reject' ? 'text-red-700' : 'text-[#212529]'
                    }`}>Reject</p>
                  </button>
                </div>
              </div>

              {/* Expiry Date (for approval) */}
              {reviewAction === 'approve' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#212529]">
                    Document Expiry Date (optional)
                  </label>
                  <Input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="border-gray-200"
                    data-testid="expiry-date-input"
                  />
                </div>
              )}

              {/* Comments (required for rejection) */}
              {reviewAction === 'reject' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#212529]">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    placeholder="Please provide a reason for rejection..."
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={3}
                    className="border-gray-200"
                    data-testid="rejection-reason"
                  />
                </div>
              )}

              {/* Optional comments for approval */}
              {reviewAction === 'approve' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#212529]">
                    Comments (optional)
                  </label>
                  <Textarea
                    placeholder="Add any notes..."
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={2}
                    className="border-gray-200"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialog({ open: false, doc: null })}
            >
              Cancel
            </Button>
            <Button
              className={`${
                reviewAction === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : reviewAction === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gray-400'
              } text-white`}
              onClick={handleReview}
              disabled={!reviewAction || submitting}
              data-testid="submit-review-btn"
            >
              {submitting ? 'Submitting...' : reviewAction === 'approve' ? 'Approve' : reviewAction === 'reject' ? 'Reject' : 'Select Action'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKYCReviewPage;
